import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { isAuthenticated, setAuth, clearAuth, getCarsSummary, validateLogin } from './api/client';
import CarSubmissions from './pages/CarSubmissions';
import CarsCatalog from './pages/CarsCatalog';

function Login({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (await validateLogin(username, password)) {
        setAuth(username, password);
        onLogin();
      } else {
        setError('Invalid credentials');
      }
    } catch {
      setError('Login failed');
    }
  };

  const field = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">CastCove Admin</h1>
        {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-2">Username</span>
            <input type="text" className={field} value={username} onChange={e => setUsername(e.target.value)} required />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-2">Password</span>
            <input type="password" className={field} value={password} onChange={e => setPassword(e.target.value)} required />
          </label>
          <button type="submit" className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 font-medium">Sign in</button>
        </form>
      </div>
    </div>
  );
}

function NavItem({ to, label, badge }: { to: string; label: string; badge?: number }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `flex items-center justify-between px-5 py-2.5 text-sm no-underline ${isActive ? 'bg-slate-700 text-white' : 'text-gray-300 hover:bg-slate-700/60'}`}
    >
      <span>{label}</span>
      {!!badge && <span className="bg-amber-500 text-white text-xs font-semibold rounded-full px-2">{badge}</span>}
    </NavLink>
  );
}

function Layout({ onLogout }: { onLogout: () => void }) {
  const [pending, setPending] = useState(0);

  useEffect(() => {
    getCarsSummary().then(r => setPending(r.data.submissions.pending)).catch(() => {});
  }, []);

  return (
    <div className="flex h-screen">
      <div className="w-52 flex-shrink-0 bg-slate-800 text-white h-screen overflow-y-auto [scrollbar-gutter:stable] flex flex-col">
        <div className="px-5 py-5 border-b border-slate-700">
          <h1 className="text-xl font-bold">CastCove Admin</h1>
          <div className="text-xs text-gray-400 mt-1">{import.meta.env.MODE === 'staging' ? 'Staging' : 'Local'}</div>
        </div>
        <nav className="py-3 flex-1">
          <NavItem to="/submissions" label="Car Submissions" badge={pending} />
          <NavItem to="/catalog" label="Car Catalog" />
        </nav>
        <button onClick={onLogout} className="m-4 px-3 py-2 text-sm text-gray-300 border border-slate-600 rounded hover:bg-slate-700">Logout</button>
      </div>
      <div className="flex-1 min-w-0 h-screen overflow-y-auto [scrollbar-gutter:stable] p-4 sm:p-6 lg:p-8 bg-gray-100">
        <Routes>
          <Route path="/submissions" element={<CarSubmissions />} />
          <Route path="/catalog" element={<CarsCatalog />} />
          <Route path="*" element={<Navigate to="/submissions" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());

  if (!authenticated) return <Login onLogin={() => setAuthenticated(true)} />;
  return <Layout onLogout={() => { clearAuth(); setAuthenticated(false); }} />;
}
